// Root App with routing, sidebar, topbar, timer
import { useState, useEffect, useMemo, useRef } from 'react';
import { useData } from './DataContext.jsx';
import { fmtMin, minToTime, fmtDeadline, deadlineDaysLeft, capitalizeName, todayIso } from './lib/format.js';
import { dayOfMonth, CHECKIN_HARD_DAY } from './lib/goals.js';
import {
  IconDashboard, IconClock, IconFolder, IconBuilding, IconTeam, IconSettings,
  IconFire, IconChevronRight, IconBell, IconSun, IconMoon, IconPlay, IconPause,
  IconChart, IconCalendar, IconDownload,
} from './icons.jsx';
import { Avatar, Toasts, Confetti, ProjectAvatar } from './components.jsx';
import { Dashboard } from './dashboard.jsx';
import { ProjectsPage, ProjectDetail } from './projects.jsx';
import { TimesPage } from './times.jsx';
import { CustomersPage, CustomerDetail } from './customers.jsx';
import { TeamPage } from './team.jsx';
import { AnalyticsPage } from './analytics.jsx';
import { SettingsPage } from './settings.jsx';
import { FEEDBACK_CATEGORIES } from './feedback-modal.jsx';
import { MENTION_SOURCE_LABELS } from './lib/mentions.js';
import { MentionText } from './mention-input.jsx';
import { ProjectFormModal } from './new-project-modal.jsx';
import { GoalBriefingModal } from './goal-briefing-modal.jsx';
import { MonthlyCheckinModal } from './monthly-checkin-modal.jsx';
import { StopTimerModal } from './stop-timer-modal.jsx';
import { ProjectClosureModal } from './project-closure-modal.jsx';
import { DeadlineOverrunModal } from './deadline-overrun-modal.jsx';
import { MissingTimesModal } from './missing-times-modal.jsx';
import { DeadlinesAdminPage } from './deadlines-admin.jsx';
import { CfoExportPage } from './cfo-export.jsx';
import { CustomerExportPage } from './customer-export.jsx';
import { AwardCeremonyModal } from './award-ceremony-modal.jsx';
import { computeWeeklyRanking, lastWeekStart, isoWeekKey } from './lib/team-ranking.js';

function Sidebar({ view, onNavigate }) {
  const { projects, customers, currentProfile, isAdmin, gamification } = useData();
  const items = [
    { k: 'dashboard', label: 'Dashboard', Icon: IconDashboard },
    { k: 'times',     label: 'Meine Zeiten', Icon: IconClock },
    { k: 'projects',  label: 'Projekte', Icon: IconFolder, count: projects.length },
    { k: 'customers', label: 'Kunden', Icon: IconBuilding, count: customers.length },
  ];
  if (isAdmin) items.push({ k: 'team', label: 'Team', Icon: IconTeam });
  if (isAdmin) items.push({ k: 'analytics', label: 'Auswertung', Icon: IconChart });
  if (isAdmin) items.push({ k: 'export', label: 'CFO-Export', Icon: IconDownload });
  if (isAdmin) items.push({ k: 'customer-export', label: 'Kundenexport', Icon: IconBuilding });
  // Deadline-Überblick ist für alle Mitarbeiter sichtbar (nur Lesen + Navigation).
  items.push({ k: 'deadlines', label: 'Deadlines', Icon: IconCalendar });
  items.push({ k: 'settings', label: 'Einstellungen', Icon: IconSettings });

  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <img src="/assets/markenegger-wordmark.png" alt="Markenegger"/>
      </div>
      <div className="sidebar-section-label">Navigation</div>
      {items.map(({ k, label, Icon, count }) => (
        <div key={k} className={`nav-item ${view === k ? 'active' : ''}`} onClick={() => onNavigate({ view: k })}>
          <Icon/> <span>{label}</span>
          {count != null && <span className="count">{count}</span>}
        </div>
      ))}
      <div className="sidebar-spacer"/>
      {gamification && (
        <div style={{
          margin: '0 6px 10px', padding: 10, borderRadius: 10,
          background: 'linear-gradient(135deg, rgba(255,107,74,0.12), rgba(255,107,74,0.04))',
          border: '1px solid var(--accent-soft-2)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div className="ic" style={{ color: 'var(--accent)', fontSize: 12, fontWeight: 600 }}>
              <IconFire size={12}/> {gamification.streak} Tage
            </div>
            <div className="tiny mono" style={{ color: 'var(--ink-2)' }}>{gamification.level}</div>
          </div>
          <div className="bar sm">
            <span style={{ width: `${gamification.progress}%`, background: 'var(--accent)' }}/>
          </div>
          <div className="tiny muted" style={{ marginTop: 6 }}>
            {gamification.nextLevel
              ? <>{gamification.xpForNext} XP bis <b style={{ color: 'var(--ink-2)' }}>{gamification.nextLevel}</b></>
              : 'Höchste Stufe erreicht'}
          </div>
        </div>
      )}
      <div className="user-card" onClick={() => onNavigate({ view: 'settings' })} title="Einstellungen">
        <Avatar user={currentProfile || { initials: '??' }}/>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="user-name">{capitalizeName(currentProfile?.full_name) || currentProfile?.email || '—'}</div>
          <div className="user-role">{isAdmin ? 'Admin' : 'Member'}</div>
        </div>
        <IconChevronRight size={12}/>
      </div>
    </aside>
  );
}

function TimerPill({ running, elapsed, projectTitle, onToggle, onChoose }) {
  const hh = String(Math.floor(elapsed / 3600)).padStart(2, '0');
  const mm = String(Math.floor((elapsed % 3600) / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');
  return (
    <div className={`timer-pill ${running ? 'running' : ''}`} onClick={!running ? onChoose : undefined}>
      <span className="dot"/>
      <span className="clock">{hh}:{mm}:{ss}</span>
      <span className="proj">{projectTitle || 'kein Projekt'}</span>
      <button className="tpbtn" onClick={(e) => { e.stopPropagation(); onToggle(); }}>
        {running ? <IconPause size={12}/> : <IconPlay size={12}/>}
      </button>
    </div>
  );
}

function fmtWhen(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Notifications popover — two tabs: urgent deadlines and incoming team feedback.
function NotificationsPopover({ onClose, onNavigate, initialTab = 'deadlines' }) {
  const { projects, customerById, myFeedback, unreadFeedbackCount, markFeedbackRead, myMentions, unreadMentionsCount, markMentionRead, profileById, projectById } = useData();
  const popoverRef = useRef(null);
  const [tab, setTab] = useState(initialTab);

  // Mark all incoming feedback read once the user looks at the Feedback tab.
  useEffect(() => {
    if (tab !== 'feedback') return;
    myFeedback.filter(f => !f.read_at).forEach(f => { markFeedbackRead(f.id).catch(() => {}); });
  }, [tab, myFeedback, markFeedbackRead]);

  // Same for @-mentions on the Erwähnungen tab.
  useEffect(() => {
    if (tab !== 'mentions') return;
    myMentions.filter(m => !m.read_at).forEach(m => { markMentionRead(m.id).catch(() => {}); });
  }, [tab, myMentions, markMentionRead]);

  const upcoming = useMemo(() => {
    const today = new Date();
    return projects
      .filter(p => (p.status === 'active' || p.status === 'closing') && p.deadline)
      .map(p => ({ p, days: deadlineDaysLeft(p.deadline, today) }))
      .sort((a, b) => a.days - b.days)
      .slice(0, 8);
  }, [projects]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) onClose();
    };
    window.addEventListener('keydown', onKey);
    // Listen on next tick so the click that opened the popover doesn't
    // immediately close it.
    const t = setTimeout(() => window.addEventListener('mousedown', onClick), 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
      clearTimeout(t);
    };
  }, [onClose]);

  function tone(days) {
    if (days < 0)  return { color: 'var(--crit)',  bg: 'var(--crit-soft)' };
    if (days < 14) return { color: 'var(--crit)',  bg: 'var(--crit-soft)' };
    if (days < 30) return { color: 'var(--warn)',  bg: 'var(--warn-soft)' };
    return            { color: 'var(--ok)',    bg: 'var(--ok-soft)'   };
  }

  return (
    <div ref={popoverRef} style={{
      position: 'absolute', right: 0, top: 46, zIndex: 100,
      width: 360,
      background: 'var(--bg-elev)',
      border: '1px solid var(--stroke-2)',
      borderRadius: 14,
      boxShadow: 'var(--shadow-lg)',
      overflow: 'hidden',
      animation: 'pop 180ms cubic-bezier(0.2,0.8,0.2,1)',
    }}>
      <div style={{ display: 'flex', borderBottom: '1px solid var(--stroke)' }}>
        {[
          { k: 'deadlines', label: 'Deadlines', count: upcoming.length },
          { k: 'mentions', label: 'Erwähnungen', count: unreadMentionsCount },
          { k: 'feedback', label: 'Feedback', count: unreadFeedbackCount },
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            style={{
              flex: 1, padding: '13px 8px', border: 0, cursor: 'pointer',
              background: 'transparent', fontFamily: 'inherit', fontSize: 13,
              fontWeight: tab === t.k ? 600 : 500,
              color: tab === t.k ? 'var(--ink)' : 'var(--ink-3)',
              borderBottom: `2px solid ${tab === t.k ? 'var(--accent)' : 'transparent'}`,
            }}
          >
            {t.label}
            {t.count > 0 && (
              <span style={{
                marginLeft: 6, padding: '1px 7px', borderRadius: 999, fontSize: 11,
                fontFamily: 'var(--mono)',
                background: (t.k === 'feedback' || t.k === 'mentions') && t.count > 0 ? 'var(--accent)' : 'var(--bg-hover)',
                color: (t.k === 'feedback' || t.k === 'mentions') && t.count > 0 ? '#fff' : 'var(--ink-3)',
              }}>{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {tab === 'mentions' && (
        myMentions.length === 0 ? (
          <div className="tiny muted" style={{ padding: 18, textAlign: 'center' }}>
            Noch keine Erwähnungen.
          </div>
        ) : (
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            {myMentions.map(m => {
              const from = profileById(m.from_user_id);
              const proj = m.project_id ? projectById(m.project_id) : null;
              const where = MENTION_SOURCE_LABELS[m.source_type] || 'Notiz';
              return (
                <div
                  key={m.id}
                  onClick={() => { if (proj) { onNavigate({ view: 'project', id: proj.id }); onClose(); } }}
                  style={{
                    padding: '12px 16px', borderTop: '1px solid var(--stroke)',
                    cursor: proj ? 'pointer' : 'default',
                    background: m.read_at ? 'transparent' : 'var(--accent-soft)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 14 }}>💬</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)' }}>
                      {from?.full_name || 'Jemand'} hat dich erwähnt
                    </span>
                    <span className="topbar-spacer" style={{ flex: 1 }}/>
                    <span className="tiny muted">{fmtWhen(m.created_at)}</span>
                  </div>
                  {m.snippet && (
                    <div style={{ fontSize: 13, color: 'var(--ink)', lineHeight: 1.45 }}>„{m.snippet}"</div>
                  )}
                  <div className="tiny muted" style={{ marginTop: 6 }}>
                    {where}{proj && <> · {proj.title}</>}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {tab === 'feedback' && (
        myFeedback.length === 0 ? (
          <div className="tiny muted" style={{ padding: 18, textAlign: 'center' }}>
            Noch kein Feedback erhalten.
          </div>
        ) : (
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            {myFeedback.map(f => {
              const cat = FEEDBACK_CATEGORIES.find(c => c.key === f.category) || FEEDBACK_CATEGORIES[3];
              const from = profileById(f.from_user_id);
              const proj = f.project_id ? projectById(f.project_id) : null;
              return (
                <div
                  key={f.id}
                  onClick={() => { if (proj) { onNavigate({ view: 'project', id: proj.id }); onClose(); } }}
                  style={{
                    padding: '12px 16px', borderTop: '1px solid var(--stroke)',
                    cursor: proj ? 'pointer' : 'default',
                    background: f.read_at ? 'transparent' : 'var(--accent-soft)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 14 }}>{cat.emoji}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: cat.color }}>{cat.label}</span>
                    <span className="topbar-spacer" style={{ flex: 1 }}/>
                    <span className="tiny muted">{fmtWhen(f.created_at)}</span>
                  </div>
                  {f.context && <div className="tiny mono muted" style={{ marginBottom: 4 }}>{f.context}</div>}
                  <div style={{ fontSize: 13, color: 'var(--ink)', lineHeight: 1.45, whiteSpace: 'pre-wrap' }}><MentionText text={f.message}/></div>
                  <div className="tiny muted" style={{ marginTop: 6 }}>
                    von {from?.full_name || 'Admin'}{proj && <> · {proj.title}</>}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {tab === 'deadlines' && (upcoming.length === 0 ? (
        <div className="tiny muted" style={{ padding: 18, textAlign: 'center' }}>
          Keine offenen Projekte mit Deadline.
        </div>
      ) : (
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          {upcoming.map(({ p, days }) => {
            const c = customerById(p.customer_id);
            const t = tone(days);
            return (
              <div
                key={p.id}
                onClick={() => { onNavigate({ view: 'project', id: p.id }); onClose(); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 16px',
                  borderTop: '1px solid var(--stroke)',
                  cursor: 'pointer',
                  transition: 'background 120ms',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <ProjectAvatar project={p} size={32}/>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.title}
                  </div>
                  <div className="tiny muted" style={{ marginTop: 2 }}>
                    {c?.name || '—'} · {fmtDeadline(p.deadline)}
                  </div>
                </div>
                <div style={{
                  padding: '4px 8px', borderRadius: 999,
                  background: t.bg, color: t.color,
                  fontSize: 11, fontWeight: 600, fontFamily: 'var(--mono)',
                  whiteSpace: 'nowrap',
                }}>
                  {days < 0 ? `${Math.abs(days)} überfällig` : `in ${days} ${days === 1 ? 'Tag' : 'Tagen'}`}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function ThemeToggle({ theme, setTheme }) {
  return (
    <button className="icon-btn" onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}>
      {theme === 'light' ? <IconMoon size={14}/> : <IconSun size={14}/>}
    </button>
  );
}

function App() {
  const {
    projects, profiles, timeEntries, absences, tasks, involvedByProject, currentProfile, projectById, unreadFeedbackCount, unreadMentionsCount,
    goalByCustomer, metricsByGoal, progressByMetric, dueGoalCheckins, dueDeadlineOverruns,
    dueMissingTimeDays,
  } = useData();
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
  const [route, setRoute] = useState(() => {
    try { return JSON.parse(localStorage.getItem('route')) || { view: 'dashboard' }; } catch { return { view: 'dashboard' }; }
  });
  const [projectModal, setProjectModal] = useState(null); // null | { project?: object } ; project present => edit mode
  const [closureModal, setClosureModal] = useState(null); // null | { project }
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerProject, setTimerProject] = useState(null);
  const [timerStartedAt, setTimerStartedAt] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [stopModal, setStopModal] = useState(null); // { from_time, to_time, project_id }
  const [goalBriefing, setGoalBriefing] = useState(null); // { projectId } — shown before timer starts
  const [checkinForce, setCheckinForce] = useState(false);   // user opened check-in manually
  const [checkinSnoozed, setCheckinSnoozed] = useState(false); // snoozed this session (soft only)
  const [overrunSnoozed, setOverrunSnoozed] = useState(false); // deadline overrun snoozed this session
  const [awardCeremony, setAwardCeremony] = useState(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [confettiKey, setConfettiKey] = useState(0);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => { localStorage.setItem('route', JSON.stringify(route)); }, [route]);

  // Default timer project = first active project
  useEffect(() => {
    if (!timerProject && projects.length > 0) {
      const firstActive = projects.find(p => p.status === 'active') || projects[0];
      setTimerProject(firstActive?.id || null);
    }
  }, [projects, timerProject]);

  useEffect(() => {
    if (!timerRunning) return;
    const id = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(id);
  }, [timerRunning]);

  // Award ceremony: first login of a new ISO week shows last week's ranking
  // for the current user. Uses localStorage to ensure once per week per user.
  useEffect(() => {
    if (!currentProfile) return;
    const lastWeek = lastWeekStart();
    const lastWeekKey = isoWeekKey(lastWeek);
    const storageKey = `award_ceremony_shown_${currentProfile.id}`;
    const alreadyShownFor = localStorage.getItem(storageKey);
    if (alreadyShownFor === lastWeekKey) return;

    const ranking = computeWeeklyRanking(profiles, timeEntries, lastWeek, {
      absences, tasks, projects, involvedByProject,
    });
    const ranked = ranking.filter(r => r.score > 0);
    if (ranked.length === 0) {
      // Nobody tracked anything last week — silently mark and skip.
      localStorage.setItem(storageKey, lastWeekKey);
      return;
    }

    const me = ranked.find(r => r.profile.id === currentProfile.id);
    if (!me) {
      // I didn't track anything last week — skip ceremony to avoid awkward shaming.
      localStorage.setItem(storageKey, lastWeekKey);
      return;
    }

    setAwardCeremony({
      rank: me.rank,
      totalRanked: ranked.length,
      percent: me.percent,
      totalMinutes: me.totalMinutes,
      weekKey: `KW ${lastWeekKey.split('-W')[1]} (${lastWeekKey.split('-W')[0]})`,
      storageKey,
      lastWeekKey,
    });
  }, [currentProfile?.id, profiles, timeEntries, absences, tasks, projects, involvedByProject]);

  function pushToast(toast) {
    const id = Math.random().toString(36);
    setToasts(t => [...t, { id, ...toast }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 4000);
  }

  function navigate(patch) {
    setRoute(prev => ({ ...prev, ...patch }));
  }

  function actuallyStartTimer() {
    setTimerRunning(true);
    setElapsed(0);
    setTimerStartedAt(new Date());
    pushToast({ emoji: '⏱️', title: 'Timer gestartet', body: projectById(timerProject)?.title });
  }

  function startTimer() {
    if (!timerProject) {
      pushToast({ emoji: '⚠️', title: 'Kein Projekt ausgewählt', body: 'Lege erst ein Projekt an, dann kannst du Zeit erfassen.' });
      return;
    }
    // If the project's customer has a goal and it hasn't been acknowledged today,
    // show the mission briefing first. One ack per customer per day so it doesn't
    // nag across that customer's projects.
    const customerId = projectById(timerProject)?.customer_id;
    const goal = customerId ? goalByCustomer[customerId] : null;
    const ackKey = `goal_ack_${currentProfile?.id}_${customerId}_${todayIso()}`;
    if (goal && !localStorage.getItem(ackKey)) {
      setGoalBriefing({ projectId: timerProject });
      return;
    }
    actuallyStartTimer();
  }

  function confirmBriefing() {
    if (!goalBriefing) return;
    const customerId = projectById(goalBriefing.projectId)?.customer_id;
    const ackKey = `goal_ack_${currentProfile?.id}_${customerId}_${todayIso()}`;
    localStorage.setItem(ackKey, '1');
    setGoalBriefing(null);
    actuallyStartTimer();
  }

  function stopTimer() {
    setTimerRunning(false);
    if (elapsed < 30) {
      pushToast({ emoji: '⏱️', title: 'Timer gestoppt', body: 'Zu kurz — nichts gespeichert.' });
      return;
    }
    const start = timerStartedAt || new Date(Date.now() - elapsed * 1000);
    const end = new Date();
    setStopModal({
      from_time: minToTime(start.getHours() * 60 + start.getMinutes()),
      to_time: minToTime(end.getHours() * 60 + end.getMinutes()),
      project_id: timerProject,
    });
  }

  const viewKey = route.view || 'dashboard';

  function renderContent() {
    if (viewKey === 'dashboard') return <Dashboard onStartTimer={startTimer} onOpenNewProject={() => setProjectModal({})} onNavigate={navigate} onOpenCheckin={() => setCheckinForce(true)} role={currentProfile?.role}/>;
    if (viewKey === 'projects') return <ProjectsPage onOpenNewProject={() => setProjectModal({})} onNavigate={navigate}/>;
    if (viewKey === 'project') return <ProjectDetail id={route.id} onBack={() => navigate({ view: 'projects' })} onStartTimer={startTimer} onEdit={(p) => setProjectModal({ project: p })} onCloseProject={(p) => setClosureModal({ project: p })}/>;
    if (viewKey === 'times') return <TimesPage onStartTimer={startTimer}/>;
    if (viewKey === 'customers') return <CustomersPage onNavigate={navigate}/>;
    if (viewKey === 'customer') return <CustomerDetail id={route.id} onBack={() => navigate({ view: 'customers' })} onNavigate={navigate} onOpenNewProject={(customerId) => setProjectModal({ defaultCustomerId: customerId })}/>;
    if (viewKey === 'team') return <TeamPage onNavigate={navigate}/>;
    if (viewKey === 'analytics') return <AnalyticsPage/>;
    if (viewKey === 'export') return <CfoExportPage/>;
    if (viewKey === 'customer-export') return <CustomerExportPage/>;
    if (viewKey === 'deadlines') return <DeadlinesAdminPage onNavigate={navigate}/>;
    if (viewKey === 'settings') return <SettingsPage/>;
  }

  // --- Monthly check-in gating (soft → hard escalation) ---------------------
  const checkinHard = dayOfMonth() >= CHECKIN_HARD_DAY;
  const snoozeKey = `goal_checkin_snooze_${currentProfile?.id}`;
  const snoozedToday = (() => { try { return localStorage.getItem(snoozeKey) === todayIso(); } catch { return false; } })();
  const checkinAutoOpen = dueGoalCheckins.length > 0 && (checkinHard || (!snoozedToday && !checkinSnoozed));
  const activeCheckin = (dueGoalCheckins.length > 0 && (checkinForce || checkinAutoOpen)) ? dueGoalCheckins[0] : null;

  function snoozeCheckin() {
    try { localStorage.setItem(snoozeKey, todayIso()); } catch {}
    setCheckinSnoozed(true);
    setCheckinForce(false);
  }

  function onCheckinSubmitted() {
    setConfettiKey(k => k + 1);
    pushToast({ emoji: '🎯', title: 'Check-in gespeichert', body: 'Stark — Zwischenstand reflektiert.' });
  }

  // --- Deadline overrun gating ----------------------------------------------
  // The owner may defer it for the current session ("Später erinnern"), but it
  // returns on the next login until they've taken a stand — so the snooze is
  // in-memory only, deliberately NOT persisted across sessions.
  const activeOverrun = (dueDeadlineOverruns.length > 0 && !overrunSnoozed)
    ? dueDeadlineOverruns[0] : null;

  // --- Missing-time gating (highest priority, NEVER dismissable) ------------
  // A skipped workday must be accounted for before anything else — it sits on
  // top of the deadline-overrun and check-in dialogs.
  const missingTimesActive = dueMissingTimeDays.length > 0;

  function snoozeOverrun() {
    setOverrunSnoozed(true);
  }

  function onOverrunSubmitted(resolution) {
    if (resolution === 'closed') {
      setConfettiKey(k => k + 1);
      pushToast({ emoji: '✅', title: 'Projekt abgeschlossen', body: 'Verzug dokumentiert.' });
    } else {
      pushToast({ emoji: '🗓️', title: 'Neue Deadline gesetzt', body: 'Verzug dokumentiert.' });
    }
  }

  return (
    <div className="app">
      <Sidebar view={viewKey} onNavigate={navigate}/>
      <div className="main" style={{ position: 'relative' }}>
        <div id="topbar-actions-anchor" style={{ position: 'absolute', right: 24, top: 14, zIndex: 50, display: 'flex', gap: 8, alignItems: 'center', pointerEvents: 'auto' }}>
          <TimerPill running={timerRunning} elapsed={elapsed}
                     projectTitle={projectById(timerProject)?.title}
                     onToggle={() => timerRunning ? stopTimer() : startTimer()}
                     onChoose={startTimer}/>
          <div style={{ position: 'relative' }}>
            <button
              className="icon-btn"
              onClick={() => setNotificationsOpen(v => !v)}
              style={notificationsOpen ? { background: 'var(--bg-hover)', color: 'var(--ink)' } : undefined}
            >
              <IconBell size={14}/>
            </button>
            {(unreadFeedbackCount + unreadMentionsCount) > 0 && (
              <span style={{
                position: 'absolute', top: -4, right: -4, minWidth: 16, height: 16,
                padding: '0 4px', borderRadius: 999, background: 'var(--accent)', color: '#fff',
                fontSize: 10, fontWeight: 700, fontFamily: 'var(--mono)',
                display: 'grid', placeItems: 'center', pointerEvents: 'none',
                border: '2px solid var(--bg-card)',
              }}>{unreadFeedbackCount + unreadMentionsCount}</span>
            )}
            {notificationsOpen && (
              <NotificationsPopover
                onClose={() => setNotificationsOpen(false)}
                onNavigate={navigate}
                initialTab={unreadMentionsCount > 0 ? 'mentions' : unreadFeedbackCount > 0 ? 'feedback' : 'deadlines'}
              />
            )}
          </div>
          <ThemeToggle theme={theme} setTheme={setTheme}/>
        </div>
        {renderContent()}
      </div>

      {awardCeremony && (
        <AwardCeremonyModal
          rank={awardCeremony.rank}
          totalRanked={awardCeremony.totalRanked}
          percent={awardCeremony.percent}
          totalMinutes={awardCeremony.totalMinutes}
          weekKey={awardCeremony.weekKey}
          onConfetti={() => setConfettiKey(k => k + 1)}
          onClose={() => {
            localStorage.setItem(awardCeremony.storageKey, awardCeremony.lastWeekKey);
            setAwardCeremony(null);
          }}
        />
      )}

      {stopModal && (
        <StopTimerModal
          fromTime={stopModal.from_time}
          toTime={stopModal.to_time}
          projectId={stopModal.project_id}
          onSave={(durationMin) => {
            setStopModal(null);
            setElapsed(0);
            pushToast({ emoji: '✅', title: `${fmtMin(durationMin)} erfasst`, body: 'Gute Arbeit.' });
          }}
          onDiscard={() => {
            setStopModal(null);
            setElapsed(0);
            pushToast({ emoji: '🗑️', title: 'Verworfen', body: 'Der Timer-Durchlauf wurde nicht gespeichert.' });
          }}
        />
      )}

      {closureModal && (() => {
        // Always read the latest project state so the modal sees status flips
        // (e.g. active → closing) without a remount
        const live = projects.find(x => x.id === closureModal.project.id);
        if (!live) return null;
        return (
          <ProjectClosureModal
            project={live}
            onClose={() => setClosureModal(null)}
            onConfetti={() => setConfettiKey(k => k + 1)}
            pushToast={pushToast}
          />
        );
      })()}

      {projectModal && (
        <ProjectFormModal
          project={projectModal.project}
          defaultCustomerId={projectModal.defaultCustomerId}
          onClose={() => setProjectModal(null)}
          onSaved={() => pushToast({
            emoji: projectModal.project ? '💾' : '🎉',
            title: projectModal.project ? 'Projekt aktualisiert' : 'Projekt angelegt!',
          })}
          onConfetti={() => { setConfettiKey(k => k + 1); pushToast({ emoji: '⭐', title: 'Vollständig geplant!', body: 'Top Planung.' }); }}
        />
      )}

      {goalBriefing && (() => {
        const proj = projectById(goalBriefing.projectId);
        const goal = proj?.customer_id ? goalByCustomer[proj.customer_id] : null;
        if (!proj || !goal) { setGoalBriefing(null); return null; }
        return (
          <GoalBriefingModal
            project={proj}
            goal={goal}
            metrics={metricsByGoal[goal.id] || []}
            progressByMetric={progressByMetric}
            onConfirm={confirmBriefing}
            onClose={() => setGoalBriefing(null)}
          />
        );
      })()}

      {/* Fehlende Zeiterfassung blockiert alles andere — Pflicht, nicht wegklickbar. */}
      {missingTimesActive && (
        <MissingTimesModal key={currentProfile?.id} days={dueMissingTimeDays} />
      )}

      {/* Deadline-Verzug hat Vorrang vor dem Monats-Check-in. */}
      {activeOverrun && !missingTimesActive && (
        <DeadlineOverrunModal
          key={`${activeOverrun.id}_${activeOverrun.deadline}`}
          project={activeOverrun}
          dismissable
          queueInfo={{ index: 0, total: dueDeadlineOverruns.length }}
          onSubmitted={onOverrunSubmitted}
          onSnooze={snoozeOverrun}
        />
      )}

      {activeCheckin && !activeOverrun && !missingTimesActive && (
        <MonthlyCheckinModal
          key={activeCheckin.goal.id}
          customer={activeCheckin.customer}
          goal={activeCheckin.goal}
          metrics={metricsByGoal[activeCheckin.goal.id] || []}
          progressByMetric={progressByMetric}
          dismissable={!checkinHard}
          queueInfo={{ index: 0, total: dueGoalCheckins.length }}
          onSubmitted={onCheckinSubmitted}
          onSnooze={snoozeCheckin}
        />
      )}

      <Toasts toasts={toasts}/>
      <Confetti trigger={confettiKey}/>
    </div>
  );
}

export default App;
